import { createContext } from 'react'
import { createContextualCan } from '@casl/react'
import { ReactNode } from 'react'
import caslAbility, { Actions, SubjectType } from 'utils/caslAbility'
import { AlmanacFilePermissions, ArticleMeta, UserRole } from 'types'
import { ArticleView } from 'types'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { useWorkspace } from 'hooks/useWorkspace'

export const AbilityContext = createContext(caslAbility)
export const BaseCan = createContextualCan(AbilityContext.Consumer)

interface CanViewProps {
  as?: UserRole[] | UserRole
  inViewMode?: ArticleView | ArticleView[]
  children: ReactNode
  inWorkspace: number
}

interface AbilityProviderProps {
  children: ReactNode
}

interface CanProps {
  a: SubjectType | Subject
  I: Actions
  children: ReactNode
  inWorkspace: number
}

export interface Subject {
  role?: UserRole
  permissions?: AlmanacFilePermissions
  version_control_mode?: boolean
  __caslSubjectType__: String
}

// use default subject if we just pass in the SubjectType otherwise, we use subject from the instance
const useDefaultSubject = (subjectType: SubjectType, workspaceId: number): Subject => {
  const workspace = useWorkspace(workspaceId) || { role: UserRole.UNAUTHED }

  return {
    role: workspace?.role || UserRole.UNAUTHED,
    __caslSubjectType__: subjectType as String,
  }
}

export const useCaslSubject = (subjectType: SubjectType, workspaceId: number, subjectInstance?: ArticleMeta) => {
  const defaultSubject = useDefaultSubject(subjectType, workspaceId)

  if (subjectType === SubjectType.ARTICLE && subjectInstance) {
    return {
      ...defaultSubject,
      permissions: subjectInstance.almanac_file.permissions,
      version_control_mode: subjectInstance.almanac_file.version_control_mode.value,
    }
  }

  return defaultSubject
}

export const Can = ({ a, I, inWorkspace: workspaceId, ...props }: CanProps) => {
  const subjectTypeValues = Object.values(SubjectType)
  const defaultCaslSubject = useDefaultSubject(a as SubjectType, workspaceId)

  const subject = subjectTypeValues.includes(a as SubjectType) ? defaultCaslSubject : a

  return <BaseCan a={subject} I={I} {...(props as any)} />
}

export const CanView = ({ as, inViewMode, children, inWorkspace: workspaceId }: CanViewProps) => {
  const articleView = useArticleView() || ArticleView.Reading
  const workspace = useWorkspace(workspaceId) || { role: UserRole.UNAUTHED }

  if (as && workspace.role && !inViewMode) {
    if ([as].flat().includes(workspace.role)) return <>{children}</>
  }

  if (inViewMode && articleView && !as) {
    if ([inViewMode].flat().includes(articleView)) return <>{children}</>
  }

  if (as && inViewMode && articleView && workspace.role) {
    if ([inViewMode].flat().includes(articleView) && [as].flat().includes(workspace.role)) {
      return <>{children}</>
    }
  }

  return <></>
}

export const canViewIn = (articleView?: ArticleView | null) => {
  return (...articleViews: ArticleView[]) => {
    // If no articleView is specified, then it's visible in all view modes
    if (!articleView) return true

    return articleViews.includes(articleView)
  }
}

export const useCanViewAs = (workspaceId: number | null) => {
  const workspace = useWorkspace(workspaceId) || { role: UserRole.UNAUTHED }

  return (userRole?: UserRole[] | UserRole) => [userRole].flat().includes(workspace.role)
}

export const AbilityProvider = ({ children }: AbilityProviderProps) => (
  <AbilityContext.Provider value={caslAbility}>{children}</AbilityContext.Provider>
)
